/**
 * A2A Client Library - Agent-to-Agent Communication
 * Version: 1.0
 * 
 * Simplified version for HR Recruitment Dashboard
 */

import { v4 as uuidv4 } from 'uuid';

export class A2AClient {
  constructor(remoteAgentUrl, agentId, options = {}) {
    this.remoteUrl = remoteAgentUrl;
    this.agentId = agentId;
    this.enableLogging = options.enableLogging || false;
    this.timeout = options.timeout || 30000;
    
    this.capabilities = null;
    
    this.log('A2A Client initialized', { remoteUrl: this.remoteUrl, agentId: this.agentId });
  }

  log(message, data = null) {
    if (this.enableLogging) {
      console.log(`[A2A Client] ${message}`, data || '');
    }
  }

  logError(message, error) {
    if (this.enableLogging) {
      console.error(`[A2A Client] ${message}`, error);
    }
  }

  /**
   * TASK 1: Discover Remote Agent Capabilities
   * TASK 3: Learn response schemas for format negotiation
   */
  async discoverCapabilities(forceRefresh = false) {
    if (this.capabilities && !forceRefresh) {
      this.log('Returning cached capabilities with schemas');
      return this.capabilities;
    }

    try {
      this.log('Discovering capabilities and response schemas...');
      const response = await fetch(`${this.remoteUrl}/a2a/discover`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          agentId: this.agentId,
          context: {},
          requestSchemas: true  // Request response schemas
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.payload && data.payload.capabilities) {
        this.capabilities = data.payload.capabilities;
        
        // Store response schemas for each capability
        this.responseSchemas = {};
        this.capabilities.forEach(cap => {
          if (cap.responseSchema) {
            this.responseSchemas[cap.name] = cap.responseSchema;
            this.log(`Learned response schema for: ${cap.name}`, cap.responseSchema);
          }
        });
        
        this.log(`Discovered ${this.capabilities.length} capabilities with response schemas`);
        return this.capabilities;
      }

      throw new Error('Invalid response format');
    } catch (error) {
      this.logError('Capability discovery failed', error);
      throw error;
    }
  }

  /**
   * TASK 3: Validate response against negotiated schema
   */
  validateResponse(taskName, response) {
    const schema = this.responseSchemas?.[taskName];
    if (!schema) {
      this.log(`No schema found for ${taskName}, skipping validation`);
      return response;
    }

    this.log(`Validating response for ${taskName} against schema`);
    
    // Check required fields
    if (schema.required) {
      for (const field of schema.required) {
        if (!(field in response)) {
          throw new Error(`Missing required field: ${field}`);
        }
      }
    }

    // Type checking for properties
    if (schema.properties) {
      for (const [key, prop] of Object.entries(schema.properties)) {
        if (key in response) {
          const value = response[key];
          const expectedType = prop.type;
          const actualType = Array.isArray(value) ? 'array' : typeof value;
          
          if (expectedType === 'number' && actualType !== 'number') {
            this.log(`Warning: Expected ${key} to be number, got ${actualType}`);
          }
          // Add more type checks as needed
        }
      }
    }

    this.log(`Response validation passed for ${taskName}`);
    return response;
  }

  /**
   * TASK 2: Execute a task on the remote agent
   * TASK 3: Validate response against negotiated schema
   */
  async executeTask(taskName, params = {}, options = {}) {
    try {
      this.log(`Executing task: ${taskName}`, params);
      
      const response = await fetch(`${this.remoteUrl}/a2a/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          task: taskName,
          data: params,
          agentId: this.agentId
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const data = await response.json();

      // Check for errors in response
      if (data.payload && data.payload.error) {
        const error = data.payload.error;
        throw new Error(`${error.code}: ${error.message}`);
      }

      // Return result with schema validation
      if (data.payload && data.payload.result !== undefined) {
        const result = data.payload.result;
        
        // TASK 3: Validate response against negotiated schema
        const validatedResult = this.validateResponse(taskName, result);
        
        this.log(`Task completed: ${taskName} (schema validated)`);
        return validatedResult;
      }

      throw new Error('Invalid response format');
    } catch (error) {
      this.logError(`Task execution failed: ${taskName}`, error);
      throw error;
    }
  }

  /**
   * TASK 4: Negotiate UX format with remote agent
   */
  async negotiateUX(context) {
    try {
      this.log('Negotiating UX format...', context);
      
      const response = await fetch(`${this.remoteUrl}/a2a/negotiate-ux`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          context,
          agentId: this.agentId
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      if (data.payload && data.payload.format) {
        this.log('UX format negotiated', data.payload.format);
        return data.payload.format;
      }

      return this.getDefaultUXFormat(context);
    } catch (error) {
      this.logError('UX negotiation failed, using defaults', error);
      return this.getDefaultUXFormat(context);
    }
  }

  getDefaultUXFormat(context) {
    const isMobile = context.device === 'mobile' || context.screenWidth < 768;
    return {
      layout: isMobile ? 'cards' : 'table',
      dataGranularity: 'detailed',
      maxItemsPerPage: isMobile ? 5 : 10,
      includeHelp: context.userLevel === 'beginner',
    };
  }

  cleanup() {
    this.log('Cleaning up A2A Client');
  }
}

export default A2AClient;
